import React, { useEffect, useState } from 'react';
import { Cookie, ShieldCheck, X } from 'lucide-react';
import {
  needsDecision,
  acceptAll,
  acceptEssentialsOnly,
  acceptCustom,
} from '../lib/consent';

// Banner de consentimiento (Chile · Ley 21.719). Aparece si no hay decisión previa.
// Layout: pastilla fija en la esquina inferior izquierda, no-modal, no bloquea.
// Botones: Aceptar todas · Solo esenciales · Personalizar.
export default function CookieBanner({ onOpenPrivacidad }) {
  const [open, setOpen] = useState(false);
  const [showCustom, setShowCustom] = useState(false);
  const [analytics, setAnalytics] = useState(true);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    // Postponemos 400ms para no golpear al usuario apenas entra.
    const t = setTimeout(() => { if (needsDecision()) setOpen(true); }, 400);
    return () => clearTimeout(t);
  }, []);

  if (!open) return null;

  const close = () => setOpen(false);
  const onAll = () => { acceptAll(); close(); };
  const onEssentials = () => { acceptEssentialsOnly(); close(); };
  const onCustom = () => { acceptCustom({ analytics, marketing }); close(); };

  return (
    <div
      data-testid="cookie-banner"
      className="fixed inset-x-3 bottom-3 sm:inset-x-0 sm:bottom-6 sm:mx-auto sm:max-w-xl z-[9999]"
      role="dialog"
      aria-live="polite"
      aria-label="Aviso de cookies y privacidad"
    >
      <div className="rounded-2xl border border-slate-200 bg-white shadow-2xl p-5 relative">
        <button
          type="button"
          data-testid="cookie-banner-close"
          onClick={onEssentials}
          className="absolute top-3 right-3 p-1.5 rounded-full text-slate-400 hover:bg-slate-100"
          aria-label="Cerrar y aceptar sólo esenciales"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="flex items-start gap-3">
          <span className="mt-0.5 w-9 h-9 rounded-xl bg-violet-100 text-brand-violet flex items-center justify-center shrink-0">
            <Cookie className="w-5 h-5" />
          </span>
          <div className="text-sm text-slate-700 leading-relaxed">
            <p className="font-bold text-slate-900 mb-1">Tu privacidad importa</p>
            <p>
              Usamos cookies esenciales para el funcionamiento del sitio y, con
              tu consentimiento, cookies de analítica para mejorar tu experiencia.
              Al continuar aceptas nuestros{' '}
              <button
                type="button"
                data-testid="cookie-banner-link-privacidad"
                onClick={() => onOpenPrivacidad && onOpenPrivacidad()}
                className="underline text-brand-violet font-semibold"
              >
                términos de privacidad
              </button>.
            </p>
          </div>
        </div>

        {showCustom && (
          <div className="mt-4 space-y-2 bg-slate-50 rounded-xl p-3 border border-slate-200" data-testid="cookie-banner-custom">
            <label className="flex items-center justify-between text-sm text-slate-700">
              <span className="inline-flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-emerald-600" />
                Esenciales <span className="text-xs text-slate-500">(siempre activas)</span>
              </span>
              <input type="checkbox" checked disabled className="h-4 w-4" />
            </label>
            <label className="flex items-center justify-between text-sm text-slate-700">
              <span>Analítica (Google Analytics / GTM)</span>
              <input
                data-testid="cookie-toggle-analytics"
                type="checkbox"
                checked={analytics}
                onChange={(e) => setAnalytics(e.target.checked)}
                className="h-4 w-4"
              />
            </label>
            <label className="flex items-center justify-between text-sm text-slate-700">
              <span>Marketing / remarketing</span>
              <input
                data-testid="cookie-toggle-marketing"
                type="checkbox"
                checked={marketing}
                onChange={(e) => setMarketing(e.target.checked)}
                className="h-4 w-4"
              />
            </label>
          </div>
        )}

        <div className="mt-4 flex flex-wrap gap-2">
          {!showCustom && (
            <button
              type="button"
              data-testid="cookie-banner-custom-open"
              onClick={() => setShowCustom(true)}
              className="px-4 py-2 rounded-full text-sm font-semibold border border-slate-300 text-slate-700 hover:bg-slate-50"
            >
              Personalizar
            </button>
          )}
          <button
            type="button"
            data-testid="cookie-banner-essentials"
            onClick={onEssentials}
            className="px-4 py-2 rounded-full text-sm font-semibold border border-slate-300 text-slate-700 hover:bg-slate-50"
          >
            Solo esenciales
          </button>
          {showCustom ? (
            <button
              type="button"
              data-testid="cookie-banner-save-custom"
              onClick={onCustom}
              className="btn-primary px-5 py-2 rounded-full text-sm font-semibold"
            >
              Guardar preferencias
            </button>
          ) : (
            <button
              type="button"
              data-testid="cookie-banner-accept-all"
              onClick={onAll}
              className="btn-primary px-5 py-2 rounded-full text-sm font-semibold"
            >
              Aceptar todas
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
